import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HighlightOutlinedIcon from "@mui/icons-material/HighlightOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import ViewStreamOutlinedIcon from "@mui/icons-material/ViewStreamOutlined";
import { brand, notice, radius } from "../../theme/businessFlowsTheme";
import { useViewConfig } from "../app/ViewConfigContext";
import { tint } from "../../utils/viewConfig";
import { accentFor, indexObjects } from "./flowStyle";
import { ask, buildIndex, conceptOfKind, diagramFacts, observation, suggestions } from "./askDiagram";

// Ask this diagram.
//
// The panel beside the drawing: what the diagram is, in its own figures; what
// it is made of, offered as chips it worked out itself; a line to type in;
// one sentence saying what was matched; and the elements themselves, each
// quoting the words it was matched on, selecting and centring on the canvas
// as the reader walks them.
//
// It draws nothing on the canvas. It hands ids up (`onHighlight`, `onFrame`,
// `onReveal`, `onPreview`) and the canvas does the rest, so this panel can
// never light up something the drawing doesn't contain.
//
// Its colours are the drawing's colours: an element that stands for an object
// the library knows wears that object type's colour here, on the shape and on
// the map, from the one place that decides it (`flowStyle.accentFor`, over the
// view configuration). An element that stands for nothing the library knows
// wears the brand's own ink, everywhere it appears.
//
// Not a word in here is about any particular model: every term a reader sees
// - every chip, every heading over a group of them, every answer, every
// result - came out of the file in front of them. The only English written
// down is the frame around it.

// Long enough that a fast typist isn't matched against every keystroke, short
// enough that it never feels like waiting. The work itself is arithmetic over
// a model already in memory, so nothing ever spins.
const SETTLE_MS = 140;

// A long list is a list nobody reads. Everything matched stays lit on the
// canvas; the rows say how many of them are printed here.
const MAX_ROWS = 60;
// Past this, results are gathered under the model's own grouping rather than
// run together as one column.
const GROUP_FROM = 8;

export const PANEL_TITLE = "Ask this diagram";
export const PANEL_PLACEHOLDER = "Find a step, a lane or a word";
export const PANEL_HELP = "Read from the drawing in front of you. Nothing is sent anywhere.";

// A mark per concept, so a chip and the shape it stands for are recognisably
// the same thing. Keyed on the concept table in `askDiagram.js` - the
// notation's own vocabulary - and never on a type name written down here.
// And a colour per concept, for the many models whose steps stand for no
// object the library knows - which is every imported file. Every value is the
// theme's own (`businessFlowsTheme`), and the two that matter most are the
// two the canvas already draws: a start is the same green and an ending the
// same red the Flow gives them, so the panel and the drawing agree.
const CONCEPT_COLOURS = {
  human: "#2f5bd3",
  automated: "#7d541a",
  decision: "#8c3a2b",
  start: "#0f7b43",
  finish: "#b42318",
  event: brand.inkSoft,
  step: brand.ink,
};

const CONCEPT_ICONS = {
  human: PersonOutlineIcon,
  automated: MemoryOutlinedIcon,
  decision: CallSplitOutlinedIcon,
  start: PlayCircleOutlineIcon,
  finish: StopCircleOutlinedIcon,
  event: CircleOutlinedIcon,
  step: CropSquareOutlinedIcon,
};

// What a suggestion stands for decides its mark: a kind of element wears the
// drawing's own vocabulary, a grouping wears a container, and a word found in
// what somebody wrote wears a text mark - because it is a word, and should
// not pretend to be a type.
const chipIcon = (chip) => {
  if (CONCEPT_ICONS[chip.conceptKey]) return CONCEPT_ICONS[chip.conceptKey];
  if (chip.source === "value") return ViewStreamOutlinedIcon;
  if (chip.source === "term") return SubjectOutlinedIcon;
  return CropSquareOutlinedIcon;
};

const resultIcon = (result) => CONCEPT_ICONS[conceptOfKind(result.kind)] || CropSquareOutlinedIcon;

// The colour of a thing that stands for no object the library knows: its
// concept's, else the panel's neutral ink. Turned into the same shape
// `flowStyle.accentFor` answers with, so everything downstream reads one way.
const fromTheme = (key) => {
  const colour = CONCEPT_COLOURS[key];
  if (!colour) return null;
  return { colour, wash: tint(colour, 0.1), line: tint(colour, 0.32) };
};

const rowId = (id) => `bf-ask-row-${String(id).replace(/[^A-Za-z0-9_-]/g, "_")}`;

// The chips, in groups a reader can read: what the diagram is made of, then
// each way it groups itself under that grouping's own name, then the words it
// repeats. Three small groups say "this was understood"; nine identical pills
// say "here is a dump".
const GROUP_HEADINGS = { kind: "It's made of", term: "It keeps coming back to" };
const GROUP_ICONS = { kind: CropSquareOutlinedIcon, value: ViewStreamOutlinedIcon, term: SubjectOutlinedIcon };

// "Divided into lanes" - the word is the file's own, so a model grouped by
// something nobody here has heard of reads just as well.
const dividedInto = (label) => `Divided into ${String(label || "").toLowerCase()}s`.replace(/ss$/, "s");

const groupChips = (chips) => {
  const groups = [];
  chips.forEach((chip) => {
    const heading = chip.source === "value" ? dividedInto(chip.hint) : GROUP_HEADINGS[chip.source];
    const found = groups.find((group) => group.heading === heading);
    if (found) found.chips.push(chip);
    else groups.push({ heading, chips: [chip], icon: GROUP_ICONS[chip.source] });
  });
  return groups;
};

// Results under the model's own grouping, once there are more of them than
// anyone reads as one column. The headings are the file's words, and the
// order is the order the results came in.
const groupResults = (results, grouping) => {
  if (results.length <= GROUP_FROM) return null;
  const by = results.every((result) => !result.context) ? "kind" : "context";
  if (by === "context" && !grouping) return null;
  const groups = [];
  results.forEach((result) => {
    const heading = (by === "context" ? result.context : result.kind) || "Everything else";
    const found = groups.find((group) => group.heading === heading);
    if (found) found.results.push(result);
    else groups.push({ heading, results: [result] });
  });
  return groups.length > 1 && groups.length < results.length ? groups : null;
};

// The words the term was found in, with the term itself picked out. Split on
// the reader's own word, so nothing is marked that they didn't ask for.
const emphasise = (text, term) => {
  const word = String(term || "").trim();
  if (!word) return [{ text, mark: false }];
  const parts = [];
  const lower = text.toLowerCase();
  const wanted = word.toLowerCase();
  let at = 0;
  for (let found = lower.indexOf(wanted, at); found >= 0; found = lower.indexOf(wanted, at)) {
    if (found > at) parts.push({ text: text.slice(at, found), mark: false });
    parts.push({ text: text.slice(found, found + wanted.length), mark: true });
    at = found + wanted.length;
  }
  if (at < text.length) parts.push({ text: text.slice(at), mark: false });
  return parts.length ? parts : [{ text, mark: false }];
};

function Marked({ text, term, ...rest }) {
  return (
    <Typography {...rest}>
      {emphasise(text, term).map((part, at) =>
        part.mark ? (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={at} component="mark" sx={{ bgcolor: brand.mark, color: "inherit", px: 0.125, borderRadius: 0.5 }}>
            {part.text}
          </Box>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={at}>{part.text}</React.Fragment>
        )
      )}
    </Typography>
  );
}

function Heading({ children, count, open, onToggle, icon: Icon }) {
  const inner = (
    <>
      {Icon && <Icon sx={{ fontSize: 13, color: brand.muted }} />}
      <Box component="span" sx={{ flex: 1, textAlign: "left" }}>
        {children}
      </Box>
      {count !== undefined && (
        <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", color: brand.muted }}>
          {count}
        </Box>
      )}
      {onToggle && (
        <ExpandMoreRoundedIcon
          sx={{ fontSize: 16, transform: open ? "none" : "rotate(-90deg)", transition: "transform .15s ease-out" }}
        />
      )}
    </>
  );
  const look = {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    width: "100%",
    fontWeight: 600,
    fontSize: "0.75rem",
    color: brand.muted,
    mb: 0.875,
  };
  if (!onToggle) {
    return (
      <Typography variant="caption" sx={look}>
        {inner}
      </Typography>
    );
  }
  return (
    <ButtonBase
      onClick={onToggle}
      aria-expanded={open}
      sx={{ ...look, px: 0.5, py: 0.375, borderRadius: radius.control, "&:hover": { bgcolor: brand.canvas } }}
    >
      {inner}
    </ButtonBase>
  );
}

function SuggestionChip({ chip, picked, accent, onPick }) {
  const Icon = chipIcon(chip);
  // The colour of the things it stands for - never one chosen here. A word
  // found in what somebody wrote has no colour, because it is a word.
  const colour = accent ? accent.colour : brand.inkSoft;
  const wash = accent ? accent.wash : brand.canvas;
  const line = accent ? accent.line : brand.line;
  return (
    <Tooltip title={chip.hint ? `${chip.label} \u00b7 ${chip.hint}` : chip.label}>
      <Chip
        className="bf-ask__chip"
        size="small"
        clickable
        onClick={() => onPick(chip)}
        icon={<Icon sx={{ fontSize: "15px !important", color: `${colour} !important` }} />}
        label={
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
            <Box
              component="span"
              sx={{ maxWidth: 148, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {chip.label}
            </Box>
            <Box
              component="span"
              sx={{
                fontVariantNumeric: "tabular-nums",
                fontSize: "0.68rem",
                fontWeight: 700,
                px: 0.5,
                borderRadius: 1,
                bgcolor: picked ? "#fff" : wash,
                color: picked ? brand.ink : brand.muted,
              }}
            >
              {chip.count}
            </Box>
          </Box>
        }
        sx={{
          height: 28,
          borderRadius: radius.control,
          border: `1px solid ${picked ? colour : line}`,
          bgcolor: picked ? tint(colour, 0.22) : "#fff",
          color: brand.ink,
          fontWeight: 600,
          fontSize: "0.74rem",
          boxShadow: picked ? `inset 0 0 0 1px ${colour}` : "none",
          "& .MuiChip-label": { pl: 0.75, pr: 1 },
          "& .MuiChip-icon": { ml: 0.875, mr: 0 },
          "&:hover": { bgcolor: picked ? tint(colour, 0.26) : wash, borderColor: colour },
          "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: 1 },
        }}
      />
    </Tooltip>
  );
}

function ResultRow({ result, accent, active, hovered, position, onPick, onPeek }) {
  const Icon = resultIcon(result);
  const colour = accent ? accent.colour : brand.ink;
  return (
    <ButtonBase
      id={rowId(result.id)}
      className="bf-ask__row"
      style={{ animationDelay: `${Math.min(position * 18, 420)}ms` }}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      onClick={() => onPick(result)}
      onMouseEnter={() => onPeek(result.id)}
      onMouseLeave={() => onPeek(null)}
      sx={{
        width: "100%",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        textAlign: "left",
        gap: 1.125,
        px: 1.25,
        py: 1,
        borderRadius: radius.control,
        border: "1px solid",
        borderColor: active ? brand.line : "transparent",
        bgcolor: active ? brand.mark : hovered ? brand.canvas : "transparent",
        boxShadow: active ? `inset 3px 0 0 ${colour}` : hovered ? `inset 3px 0 0 ${accent ? accent.line : brand.line}` : "none",
        transition: "background-color .12s ease-out",
        "&:hover": { bgcolor: active ? brand.mark : brand.canvas },
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          mt: 0.125,
          flexShrink: 0,
          borderRadius: 1.5,
          display: "grid",
          placeItems: "center",
          bgcolor: accent ? accent.wash : brand.canvas,
          color: colour,
        }}
      >
        <Icon sx={{ fontSize: 15 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Marked
          text={result.name || result.kind || result.caption}
          term={result.quote ? null : result.byTerm && [...result.byTerm.keys()][0]}
          sx={{
            fontWeight: 650,
            fontSize: "0.84rem",
            lineHeight: 1.35,
            color: result.name ? brand.ink : brand.muted,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        />
        <Stack direction="row" alignItems="center" spacing={0.625} sx={{ minWidth: 0, mt: 0.25 }}>
          <Typography variant="caption" sx={{ color: brand.muted, flexShrink: 0 }}>
            {result.kind}
          </Typography>
          {result.context && (
            <Box
              component="span"
              title={result.context}
              sx={{
                fontSize: "0.68rem",
                fontWeight: 600,
                lineHeight: 1.6,
                px: 0.625,
                borderRadius: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                bgcolor: accent ? accent.wash : brand.canvas,
                color: accent ? brand.ink : brand.muted,
                border: `1px solid ${accent ? accent.line : brand.hairline}`,
              }}
            >
              {result.context}
            </Box>
          )}
          {result.written && (
            <SubjectOutlinedIcon
              titleAccess="Somebody wrote this one up"
              sx={{ fontSize: 13, color: brand.muted, flexShrink: 0 }}
            />
          )}
        </Stack>
        {result.quote && (
          <Marked
            text={result.quote.text}
            term={result.quote.term}
            variant="caption"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mt: 0.5,
              pl: 1,
              borderLeft: `2px solid ${accent ? accent.line : brand.line}`,
              color: brand.prose,
              lineHeight: 1.45,
            }}
          />
        )}
      </Box>
    </ButtonBase>
  );
}

function Quiet({ tone = "quiet", children }) {
  const look = notice[tone] || notice.quiet;
  return (
    <Box
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: radius.control,
        bgcolor: look.bg,
        border: `1px solid ${look.line}`,
        color: look.fg,
      }}
    >
      {children}
    </Box>
  );
}

function FootButton({ children, ...rest }) {
  return (
    <ButtonBase
      {...rest}
      sx={{
        px: 1,
        py: 0.5,
        gap: 0.5,
        borderRadius: radius.control,
        fontSize: "0.73rem",
        fontWeight: 650,
        color: brand.ink,
        border: `1px solid ${brand.line}`,
        "&:hover": { bgcolor: brand.canvas, borderColor: brand.inkSoft },
        "&:focus-visible": { outline: `2px solid ${brand.ink}` },
      }}
    >
      {children}
    </ButtonBase>
  );
}

/**
 * @param {Array} elements one entry per element on the canvas, as
 *   `BpmnCanvas` reads them (`{ id, name, kind, rows }`).
 * @param {Array} objects the objects the library knows about here, so a row
 *   can wear the same colour its shape does.
 * @param {string} title what is being asked about.
 * @param {(ids: string[]) => void} onHighlight what to light up; `[]` clears.
 * @param {(ids: string[], options) => void} onFrame move the view to them.
 * @param {(id: string, options) => void} onReveal select one and centre it.
 * @param {(id: string | null) => void} onPreview a soft emphasis under the
 *   pointer, without moving anything.
 * @param {string} pointedAt the element the pointer is over on the canvas, so
 *   the list and the drawing agree about what is being looked at.
 */
export default function AskPanel({
  elements,
  objects,
  title,
  onHighlight,
  onFrame,
  onReveal,
  onPreview,
  onClose,
  pointedAt,
}) {
  const config = useViewConfig();
  const [query, setQuery] = useState("");
  const [settled, setSettled] = useState("");
  const [cursor, setCursor] = useState(-1);
  const [closed, setClosed] = useState({});
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const index = useMemo(() => buildIndex(elements), [elements]);
  const chips = useMemo(() => suggestions(index), [index]);
  const facts = useMemo(() => diagramFacts(index), [index]);
  const sentence = useMemo(() => observation(index), [index]);
  const answer = useMemo(() => ask(index, settled, { suggestions: chips }), [index, settled, chips]);
  // The one place a colour comes from, for the panel as for the canvas.
  const known = useMemo(() => indexObjects(objects), [objects]);
  const byId = useMemo(() => new Map(index.elements.map((element) => [element.id, element])), [index]);
  const accentOf = useCallback(
    (result) =>
      accentFor(config, known, { guid: String(result.id).replace(/^ID-/, ""), name: result.name }) ||
      fromTheme(conceptOfKind(result.kind)),
    [config, known]
  );
  // A chip's colour is the commonest colour of the things it picks out, so
  // the chip, its rows and the shapes on the canvas are one thing. A chip
  // whose elements the library knows nothing about has none, and falls back
  // to the theme's own ink.
  const chipAccent = useCallback(
    (chip) => {
      const counts = new Map();
      (chip.ids || []).forEach((id) => {
        const element = byId.get(id);
        if (!element) return;
        const accent = accentOf(element);
        if (!accent) return;
        const seen = counts.get(accent.colour) || { accent, found: 0 };
        seen.found += 1;
        counts.set(accent.colour, seen);
      });
      const best = [...counts.values()].sort((a, b) => b.found - a.found)[0];
      if (best) return best.accent;
      // A chip standing for a concept wears that concept's colour even where
      // it picks out nothing the library knows.
      return fromTheme(chip.conceptKey);
    },
    [byId, accentOf]
  );

  // Opened deliberately, so the line to type in is where the keyboard lands.
  // Closing puts it back on the button that opened it (the viewer does that).
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // A different drawing is a different question: nothing carries over.
  useEffect(() => {
    setQuery("");
    setSettled("");
    setCursor(-1);
  }, [elements]);

  useEffect(() => {
    if (query === settled) return undefined;
    const timer = setTimeout(() => setSettled(query), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [query, settled]);

  // The canvas follows the answer, and only the answer: one place decides what
  // is lit, so nothing can leave the drawing dimmed with no question in force.
  const ids = answer.ids;
  const signature = ids.join(",");
  useEffect(() => {
    onHighlight(ids);
    setCursor(ids.length ? 0 : -1);
    setClosed({});
    // The view moves to the whole of what matched, so one lit step in a wide
    // drawing doesn't have to be hunted for. The canvas decides how far.
    if (ids.length) onFrame(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  useEffect(
    () => () => {
      onHighlight([]);
      onPreview(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const shown = answer.results.slice(0, MAX_ROWS);
  const groups = groupResults(shown, index.grouping);

  const pick = useCallback(
    (position) => {
      if (!shown.length) return;
      const next = Math.max(0, Math.min(shown.length - 1, position));
      setCursor(next);
      onReveal(shown[next].id, { select: true });
      const row = listRef.current?.querySelector(`[id="${rowId(shown[next].id)}"]`);
      // Not every browser (and no test renderer) has it; the row is chosen
      // either way, and the canvas is where the reader is looking.
      row?.scrollIntoView?.({ block: "nearest" });
    },
    [shown, onReveal]
  );

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      pick(cursor + 1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      pick(cursor <= 0 ? 0 : cursor - 1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      pick(cursor < 0 ? 0 : cursor);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      if (query) {
        setQuery("");
        setSettled("");
        return;
      }
      onClose();
    }
  };

  const runChip = (chip) => {
    setQuery(chip.query);
    setSettled(chip.query);
    inputRef.current?.focus();
  };

  const clear = () => {
    setQuery("");
    setSettled("");
    inputRef.current?.focus();
  };

  const empty = !index.size;
  const idle = answer.mode === "idle";
  const at = (result) => shown.indexOf(result);

  const row = (result) => (
    <ResultRow
      key={result.id}
      result={result}
      accent={accentOf(result)}
      active={at(result) === cursor}
      hovered={pointedAt === result.id}
      position={at(result)}
      onPick={() => pick(at(result))}
      onPeek={onPreview}
    />
  );

  return (
    <Box
      className="bf-ask"
      sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, bgcolor: "#fff" }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ px: 2, pt: 1.75, pb: 0.5 }}>
        <HighlightOutlinedIcon sx={{ fontSize: 19, color: brand.ink, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.3 }}>
            {PANEL_TITLE}
          </Typography>
          {title && (
            <Typography
              variant="caption"
              title={title}
              sx={{ display: "block", color: brand.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {title}
            </Typography>
          )}
        </Box>
        <Tooltip title="Close">
          <IconButton size="small" aria-label="Close" onClick={onClose} sx={{ color: brand.muted }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {empty ? (
        <Box sx={{ px: 2, pb: 2 }}>
          <Quiet>
            <Typography variant="body2">Nothing has been drawn here to look through.</Typography>
          </Quiet>
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <TextField
            inputRef={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            size="small"
            fullWidth
            autoComplete="off"
            placeholder={PANEL_PLACEHOLDER}
            inputProps={{
              "aria-label": PANEL_TITLE,
              role: "combobox",
              "aria-expanded": shown.length > 0,
              "aria-controls": "bf-ask-results",
              "aria-activedescendant": cursor >= 0 && shown[cursor] ? rowId(shown[cursor].id) : undefined,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 18, color: brand.muted }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear" onClick={clear} sx={{ color: brand.muted }}>
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: radius.control,
                bgcolor: brand.shell,
                fontSize: "0.85rem",
              },
            }}
          />
        </Box>
      )}

      {/* What this drawing is, before anybody asks it anything: its own
          figures, and one true sentence about them. */}
      {idle && !empty && (
        <Box sx={{ px: 2, pb: 1.75 }}>
          <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 2.5, pb: 1.5 }}>
            {facts.map((fact) => (
              <Box key={fact.label}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
                  {fact.value}
                </Typography>
                <Typography variant="caption" sx={{ display: "block", color: brand.muted, mt: 0.125 }}>
                  {fact.label}
                </Typography>
              </Box>
            ))}
          </Stack>
          {sentence && (
            <Typography sx={{ fontSize: "0.875rem", lineHeight: 1.55, color: brand.prose, fontWeight: 450 }}>
              {sentence}
            </Typography>
          )}
        </Box>
      )}

      {!empty && !idle && (
        <Box sx={{ px: 2, pb: 1 }}>
          {answer.mode === "question" ? (
            <Quiet tone="wait">
              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                {answer.answer}
              </Typography>
              {answer.note && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.75, opacity: 0.9 }}>
                  {answer.note}
                </Typography>
              )}
            </Quiet>
          ) : (
            <Typography
              data-testid="bf-ask-answer"
              variant="body2"
              sx={{ color: answer.mode === "empty" ? brand.muted : brand.ink, fontWeight: 600, lineHeight: 1.45 }}
            >
              {answer.answer}
            </Typography>
          )}
          {answer.mode === "empty" && answer.nearest && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Typography variant="caption" sx={{ color: brand.muted }}>
                Closest here:
              </Typography>
              <SuggestionChip chip={answer.nearest} picked={false} accent={chipAccent(answer.nearest)} onPick={runChip} />
            </Stack>
          )}
        </Box>
      )}

      {/* The body: what the diagram contains until a question is asked, and
          what it answered once one is. */}
      <Box
        id="bf-ask-results"
        ref={listRef}
        role="listbox"
        aria-label="What matched"
        className="bf-ask__results"
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.25, pb: 1 }}
      >
        {idle && !empty ? (
          <Stack spacing={2} sx={{ px: 0.75, pt: 0.5 }}>
            {groupChips(chips).map((group) => (
              <Box key={group.heading}>
                <Heading icon={group.icon}>{group.heading}</Heading>
                <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 0.625 }}>
                  {group.chips.map((chip) => (
                    <SuggestionChip
                      key={chip.id}
                      chip={chip}
                      picked={settled === chip.query}
                      accent={chipAccent(chip)}
                      onPick={runChip}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <>
            {groups ? (
              <Stack spacing={1.25} sx={{ pt: 0.5 }}>
                {groups.map((group) => {
                  const open = !closed[group.heading];
                  return (
                    <Box key={group.heading}>
                      <Heading
                        count={group.results.length}
                        open={open}
                        onToggle={() => setClosed((was) => ({ ...was, [group.heading]: open }))}
                      >
                        {group.heading}
                      </Heading>
                      <Collapse in={open} unmountOnExit>
                        <Stack spacing={0.25}>{group.results.map(row)}</Stack>
                      </Collapse>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Stack spacing={0.25}>{shown.map(row)}</Stack>
            )}
            {answer.results.length > shown.length && (
              <Typography variant="caption" sx={{ display: "block", px: 1.25, pt: 1, color: brand.muted }}>
                Showing the first {shown.length} of {answer.results.length}. All of them are lit on the drawing.
              </Typography>
            )}
          </>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ px: 2, py: 1.25, borderTop: `1px solid ${brand.hairline}`, color: brand.muted }}
      >
        <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }}>
          {idle || empty ? PANEL_HELP : `${answer.results.length} of ${index.size} lit`}
        </Typography>
        {!idle && !empty && answer.results.length > 0 && (
          <Tooltip title="Move the view back to what matched">
            <span>
              <FootButton onClick={() => onFrame(ids, { force: true })} aria-label="Fit the view to what matched">
                <CenterFocusStrongOutlinedIcon sx={{ fontSize: 15 }} />
                Fit
              </FootButton>
            </span>
          </Tooltip>
        )}
        {!idle && !empty && <FootButton onClick={clear}>Clear</FootButton>}
      </Stack>
    </Box>
  );
}
